import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type FinanceAllocationBody, type FinanceCollections } from "../api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

export function FinanceAllocation({ clientId }: { clientId: number }) {
  const { t } = useTranslation("qarzlar");
  const [available, setAvailable] = useState(false);
  const [history, setHistory] = useState(false);
  const [data, setData] = useState<FinanceCollections | null>(null);
  const [collection, setCollection] = useState("");
  const [document, setDocument] = useState("");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [frozen, setFrozen] = useState(false);
  const pending = useRef<FinanceAllocationBody | null>(null);
  const uncertain = useRef(false);

  useEffect(() => {
    let alive = true;
    api.financeStatus().then(async (status) => {
      if (!alive) return;
      setAvailable(status.allocation_available);
      setHistory(status.allocation_history_available);
      if (status.allocation_history_available) {
        const rows = await api.financeCollections(clientId);
        if (alive) setData(rows);
      }
    }).catch(() => { /* Unavailable foundations add no disabled-workflow prompt. */ });
    return () => { alive = false; };
  }, [clientId]);

  if (!history || !data) return null;
  const released = new Set(data.allocations.map((row) => row.reverses_id));
  async function save() {
    if (busy) return;
    if (!pending.current) {
      const [kind, id] = document.split(":");
      pending.current = { collection_id: Number(collection), amount, reason,
        operation_key: crypto.randomUUID(),
        ...(kind === "work" ? { work_id: Number(id) } : { sale_id: Number(id) }) };
    }
    setBusy(true); setFrozen(true); setError("");
    try {
      await api.financeAllocate(pending.current);
      pending.current = null; uncertain.current = false; setFrozen(false); setAmount(""); setReason("");
      setData(await api.financeCollections(clientId));
    } catch (e) {
      const failure = e as { status?: number; message?: string };
      const conclusive = failure.status !== undefined && [400, 404, 409, 422].includes(failure.status);
      if (conclusive && !uncertain.current) { pending.current = null; setFrozen(false); }
      else { uncertain.current = true; }
      setError(failure.message || t("finance.error"));
    } finally { setBusy(false); }
  }

  return <section className="space-y-3 border-t pt-4">
    <h3 className="font-medium">{t("finance.title")}</h3>
    <p className="text-sm text-muted-foreground">{t("finance.help")}</p>
    <p className="text-xs text-muted-foreground">{t("finance.limit")}</p>
    {data.documents.filter((row) => row.unresolved || Number(row.excess) > 0).map((row) =>
      <p key={`review:${row.kind}:${row.id}`} className="text-sm text-destructive">
        {t(`finance.${row.kind}`)} #{row.id} · {row.unresolved ? t("finance.unresolvedReturn") : t("finance.returnExcess", { amount: row.excess })}
      </p>)}
    {data.collections.filter((row) => row.unresolved || Number(row.consumed) > 0).map((row) =>
      <p key={`consumed:${row.id}`} className="text-xs text-muted-foreground">
        #{row.id} · {row.unresolved ? t("finance.unresolvedReturn") : t("finance.returnConsumed", { amount: row.consumed })}
      </p>)}
    {available && <fieldset disabled={busy || frozen} className="space-y-2">
      <label className="block text-sm">{t("finance.collection")}
        <select className="block w-full rounded border bg-background p-2" value={collection} onChange={(e) => setCollection(e.target.value)}>
          <option value="">{t("finance.choose")}</option>
          {data.collections.filter((row) => Number(row.unallocated) > 0 && !row.unresolved && !Number(row.excess)).map((row) =>
            <option key={row.id} value={row.id}>#{row.id} · {row.unallocated} · {row.credit ? t("finance.credit") : t("finance.collection")}</option>)}
        </select>
      </label>
      <label className="block text-sm">{t("finance.document")}
        <select className="block w-full rounded border bg-background p-2" value={document} onChange={(e) => setDocument(e.target.value)}>
          <option value="">{t("finance.choose")}</option>
          {data.documents.filter((row) => Number(row.outstanding) > 0 && !row.unresolved && !Number(row.excess)).map((row) =>
            <option key={`${row.kind}:${row.id}`} value={`${row.kind}:${row.id}`}>{t(`finance.${row.kind}`)} #{row.id} · {row.outstanding}</option>)}
        </select>
      </label>
      <label className="block text-sm">{t("finance.amount")}<Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} /></label>
      <label className="block text-sm">{t("finance.reason")}<Input value={reason} onChange={(e) => setReason(e.target.value)} /></label>
    </fieldset>}
    {available && <Button disabled={busy || !collection || !document || !amount || !reason.trim()} onClick={save}>
      {frozen ? t("finance.retry") : t("finance.allocate")}
    </Button>}
    {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
    {data.allocations.filter((row) => Number(row.amount) > 0 && !released.has(row.id)).map((row) =>
      <div key={row.id} className="flex items-center justify-between gap-2 text-sm">
        <span>#{row.collection_id} → {t(row.work_id ? "finance.work" : "finance.sale")} #{row.work_id ?? row.sale_id} · {row.amount}</span>
        <Button variant="outline" disabled={busy || frozen} onClick={async () => {
          if (!reason.trim()) { setError(t("finance.reason")); return; }
          setBusy(true); setError("");
          try { await api.financeRelease(row.id, reason); setData(await api.financeCollections(clientId)); }
          catch (e) { setError(e instanceof Error ? e.message : t("finance.error")); }
          finally { setBusy(false); }
        }}>{t("finance.release")}</Button>
      </div>)}
    {!available && <label className="block text-sm">{t("finance.reason")}<Input value={reason} onChange={(e) => setReason(e.target.value)} /></label>}
  </section>;
}
